import { apiFetch } from "@/lib/api";

export const WIRELESS_ISSUE_QUERY_KEY = ["manufacturing", "wireless-issue", "workspace"] as const;

export interface WirelessIssueReplicaMeta {
  sourceSchema: "wireless_prod";
  sourceTable: "device_records";
  latestRecordAt: string | null;
  totalRecordCount: number;
}

export interface WirelessIssueRecentCheckin {
  checked_in_at: string;
  gps_lat: number | null;
  gps_long: number | null;
}

export interface WirelessIssueRecentCoreSaturationEvent {
  event_timestamp: string;
  event_type: string | null;
  fault_current: number | null;
  prefault_current: number | null;
  recover_current: number | null;
}

export interface WirelessIssuePcbaNotePreview {
  text: string;
  created_at: string;
  author_label: string;
  deleted: boolean;
}

export interface WirelessIssueWorkspaceRow {
  pcba_serial: string;
  serial_fci_id: number;
  device_label_serial: string | null;
  imei: string | null;
  finished_sku: string | null;
  origin_batch_id: number;
  origin_batch_item_id: number | null;
  origin_batch_line_sku: string | null;
  origin_shipment_id: number | null;
  origin_shipment_date: string | null;
  origin_ship_to_label: string | null;
  current_shipment_id: number | null;
  current_shipment_date: string | null;
  current_ship_to_label: string | null;
  current_shipment_end_user: string | null;
  flagged: boolean;
  flag_updated_at: string | null;
  flag_updated_by_email: string | null;
  wireless_portal_org_name: string | null;
  wireless_portal_group_names: string | null;
  wireless_portal_fci_serial: string | null;
  wireless_portal_imei: string | null;
  wireless_portal_status: string | null;
  wireless_portal_shipment_date: string | null;
  checkins_last_7_days: number;
  checkins_last_7_days_post_mfg: number;
  max_checkins_24h_post_mfg: number;
  problem_tier_status: 1 | 2 | 3 | "normal" | "in_factory" | "no_imei";
  problem_tier: 1 | 2 | 3 | null;
  problem_tier_reasons: string[];
  recent_checkins: WirelessIssueRecentCheckin[];
  core_saturation_events_14d: number;
  recent_core_saturation_events: WirelessIssueRecentCoreSaturationEvent[];
  note_count: number;
  latest_note: WirelessIssuePcbaNotePreview | null;
}

export interface WirelessIssueBatchPeek {
  batch_id: number;
  mfr_ship_by_date: string | null;
  ship_to_label: string | null;
  shipment_on_hold: boolean;
  serial_count: number;
  line_count: number;
}

export interface WirelessIssueWorkspaceResponse {
  batchIds: number[];
  seedFlaggedPcbaSerials: string[];
  replicaMeta: WirelessIssueReplicaMeta;
  batchPeeks: Record<number, WirelessIssueBatchPeek>;
  rows: WirelessIssueWorkspaceRow[];
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
}

export async function fetchWirelessIssueWorkspace(options?: {
  refresh?: boolean;
  background?: boolean;
}): Promise<WirelessIssueWorkspaceResponse> {
  return apiFetch<WirelessIssueWorkspaceResponse>("/api/manufacturing/wireless-issue/workspace", {
    query: {
      ...(options?.refresh ? { refresh: "1" } : {}),
      ...(options?.background ? { background: "1" } : {}),
    },
  });
}

export async function setWirelessIssueFlag(input: {
  pcbaSerial: string;
  flagged: boolean;
  note?: string | null;
}): Promise<{ ok: true; flag: { pcba_serial: string; flagged: boolean; updated_at: string; updated_by_email: string } }> {
  return apiFetch("/api/manufacturing/wireless-issue/flags", {
    method: "POST",
    body: input,
  });
}
